/**
 * Free Tips Card — surfaces the 2 highest-confidence picks of the day
 * directly inside the Scores app. Anonymous users get a real taste of
 * the prediction quality, then the "Unlock N more" CTA funnels them to
 * a 14-day free trial of SmartKick Predictions.
 *
 * Pulls from /api/predictions/preview which already returns:
 *   { visible: [<top 2 picks>], blurred: [<6 more>], total_today: N }
 */
import { useEffect, useState } from 'react';
import { Zap, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'sk_scores_tips_dismissed_at';
const ONE_DAY = 24 * 60 * 60 * 1000;

const ScoresFreeTipsCard = ({ onCtaClick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - t < ONE_DAY;
  });

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    fetch(`${API_URL}/api/predictions/preview`)
      .then((r) => (r.ok ? r.json() : { visible: [] }))
      .then((j) => {
        if (!alive) return;
        setData(j);
        if (j && j.visible && j.visible.length > 0) {
          trackEvent('prediction_view', { source: 'scores_free_tips' });
        }
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [dismissed]);

  if (dismissed) return null;
  if (loading) return null;
  if (!data || !data.visible || data.visible.length === 0) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const moreCount = Math.max(0, (data.total_today || 0) - 2);
  const picks = data.visible.slice(0, 2);

  return (
    <div className="mx-3 mt-3 mb-2 rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/8 via-emerald-500/4 to-transparent overflow-hidden" data-testid="free-tips-card">
      <div className="flex items-start gap-2 px-3 pt-3 pb-2">
        <div className="shrink-0 w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center">
          <Zap className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black tracking-[0.18em] uppercase text-emerald-400 leading-tight">
            SmartKick AI · Free Tips Today
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">Our 2 highest-confidence picks · no signup</div>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 w-7 h-7 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-500"
          aria-label="Dismiss tips card"
          data-testid="free-tips-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-3 pb-3 space-y-2">
        {picks.map((p) => (
          <div key={p.id} className="rounded-xl bg-black/30 border border-white/5 p-2.5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-zinc-500 truncate">{p.competition}</span>
              <span className="text-[10px] font-black text-emerald-400">{p.confidence}% CONF</span>
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-white truncate mb-0.5">
              {p.home_team_crest && <img src={p.home_team_crest} alt="" className="w-3.5 h-3.5 object-contain" />}
              <span className="truncate">{p.home_team}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[13px] text-white truncate mb-1.5">
              {p.away_team_crest && <img src={p.away_team_crest} alt="" className="w-3.5 h-3.5 object-contain" />}
              <span className="truncate">{p.away_team}</span>
            </div>
            <div className="flex items-center justify-between border-t border-white/5 pt-1.5 mt-1">
              <span className="text-[11px] text-zinc-500">Pick</span>
              <span className="text-[12px] font-black text-emerald-400">
                {p.predicted_result} {p.predicted_score && `(${p.predicted_score})`}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10 px-3.5 py-3 bg-black/20" data-testid="free-tips-gate">
        <div className="flex items-center gap-2 mb-0.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-[13px] font-black text-white">You've viewed today's free picks</span>
        </div>
        <p className="text-[12px] text-zinc-400 leading-snug mb-2.5 pl-6">
          Unlock {moreCount > 0 ? `${moreCount} more predictions` : 'all predictions'}, value bets and player markets.
        </p>
        <button
          onClick={onCtaClick}
          className="w-full px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-extrabold flex items-center justify-center gap-1.5 transition-colors"
          data-testid="free-tips-unlock-cta"
        >
          Start 14 days free
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ScoresFreeTipsCard;
